import { createEl } from '../lib/dom';
import { sampleUnique } from '../lib/random';
import { SITE } from '../site/site-config';
import type { AppTestimonial } from '../site/site-config';

const SAMPLE_SIZE = 3;

function clampRating(r: AppTestimonial['rating']): number {
  if (r == null || r < 1 || r > 5) {
    return 5;
  }
  return r;
}

function avatarVariant(roleLabel: string): 'client' | 'collab' | 'company' {
  const r = roleLabel.toLowerCase();
  if (r.includes('empresa')) {
    return 'company';
  }
  if (r.includes('colaborador')) {
    return 'collab';
  }
  return 'client';
}

function commentAvatar(roleLabel: string): HTMLElement {
  const v = avatarVariant(roleLabel);
  const wrap = createEl('div', {
    className: `sv-comment__avatar sv-comment__avatar--${v}`,
    'aria-hidden': 'true',
  });
  wrap.innerHTML = `<svg class="sv-comment__avatar-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" focusable="false">
    <path fill="currentColor" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
  </svg>`;
  return wrap;
}

function starRow(rating: number): HTMLElement {
  const row = createEl('div', {
    className: 'sv-comment__stars',
    role: 'img',
    'aria-label': `Valoración: ${rating} de 5 estrellas`,
  });
  for (let i = 1; i <= 5; i += 1) {
    row.appendChild(
      createEl(
        'span',
        {
          className: `sv-comment__star${i <= rating ? ' sv-comment__star--on' : ' sv-comment__star--off'}`,
          'aria-hidden': 'true',
        },
        ['★']
      )
    );
  }
  return row;
}

export function renderComments(target: HTMLElement): void {
  const grid = createEl('div', {
    className: 'sv-comments-grid',
    id: 'sv-comments-grid',
    role: 'list',
  });

  const section = createEl(
    'section',
    {
      className: 'sv-section sv-section--surface sv-comments-section',
      id: 'comentarios',
      'aria-labelledby': 'comments-title',
    },
    [
      createEl('div', { className: 'sv-inner' }, [
        createEl('h2', { className: 'sv-section__title', id: 'comments-title' }, [
          SITE.commentsSectionTitle,
        ]),
        createEl('p', { className: 'sv-comments-section__subtitle' }, [
          SITE.commentsSectionSubtitle,
        ]),
        grid,
        createEl('div', { className: 'sv-comments-actions' }, [
          createEl(
            'button',
            {
              type: 'button',
              className: 'sv-btn sv-btn--outline-dark sv-comments-refresh',
              id: 'sv-comments-refresh',
            },
            [SITE.commentsRefreshLabel]
          ),
        ]),
      ]),
    ]
  );

  target.appendChild(section);
  mountCommentCards(grid);
  const btn = document.getElementById('sv-comments-refresh');
  btn?.addEventListener('click', () => mountCommentCards(grid));
}

function mountCommentCards(grid: HTMLElement): void {
  const pool = SITE.testimonials as readonly AppTestimonial[];
  const picks = sampleUnique(pool, SAMPLE_SIZE);
  grid.replaceChildren();
  for (const t of picks) {
    grid.appendChild(commentCard(t));
  }
}

function commentCard(t: AppTestimonial): HTMLElement {
  const rating = clampRating(t.rating);
  const head = createEl('div', { className: 'sv-comment__head' }, [
    commentAvatar(t.roleLabel),
    starRow(rating),
  ]);

  return createEl('blockquote', { className: 'sv-comment', role: 'listitem' }, [
    head,
    createEl('p', { className: 'sv-comment__text' }, [`“${t.text}”`]),
    createEl('footer', { className: 'sv-comment__footer' }, [
      createEl('cite', { className: 'sv-comment__author' }, [t.author]),
      createEl('span', { className: 'sv-comment__role' }, [t.roleLabel]),
    ]),
  ]);
}
